/**
 * @file simple-pagination.tsx
 * @module Global/simple-pagination
 * @description Reusable pagination component for list views.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import React, { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import clsx from "clsx";

interface SimplePaginationProps {
  currentPage: number;
  onPageChange: (page: number) => void;
  hasMore: boolean;
  loading?: boolean;
}

/**
 * A minimalist, premium pagination component with only Prev and Next buttons.
 * Manages page counter locally and notifies parent on changes.
 */
const SimplePagination: React.FC<SimplePaginationProps> = ({
  currentPage,
  onPageChange,
  hasMore,
  loading = false,
}) => {
  const [localPage, setLocalPage] = useState(currentPage);

  useEffect(() => {
    setLocalPage(currentPage);
  }, [currentPage]);

  const handlePrev = () => {
    if (localPage > 1) {
      const newPage = localPage - 1;
      setLocalPage(newPage);
      onPageChange(newPage);
    }
  };

  const handleNext = () => {
    if (hasMore) {
      const newPage = localPage + 1;
      setLocalPage(newPage);
      onPageChange(newPage);
    }
  };

  return (
    <div className="d-flex align-items-center justify-content-between my-9 px-4 py-5 bg-white rounded-xl shadow-sm border border-gray-100 mb-10">
      <div className="d-flex align-items-center bg-light-primary px-5 py-2 rounded-pill">
        <span className="text-primary fs-14 fw-bold">
          Page <span className="fs-16 fw-bolder ms-1">{localPage}</span>
        </span>
      </div>
      
      <div className="d-flex align-items-center gap-4">
        <Button
          variant="light"
          className={clsx(
            "uw-btn uw-btn-light btn-active-light-primary fw-bold px-7 py-3 transition-all duration-300",
            (localPage <= 1 || loading) && "opacity-50 grayscale pointer-events-none"
          )}
          onClick={handlePrev}
          disabled={localPage <= 1 || loading}
        >
          <span className="d-flex align-items-center">
             <i className="ki-duotone ki-left fs-3 me-2"></i>
             Previous
          </span>
        </Button>

        <Button
          variant="light"
          className={clsx(
            "uw-btn uw-btn-light btn-active-light-primary fw-bold px-8 py-3 transition-all duration-300",
            (!hasMore || loading) && "opacity-50 grayscale pointer-events-none"
          )}
          onClick={handleNext}
          disabled={!hasMore || loading}
        >
          <span className="d-flex align-items-center">
            Next
            <i className="ki-duotone ki-right fs-3 ms-2"></i>
          </span>
        </Button>
      </div>
    </div>
  );
};

export default SimplePagination;
